import React, {
  useState, useCallback, useReducer, useEffect,
} from 'react';
import PropTypes from 'prop-types';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  InfoTooltipButton,
  Textarea,
  Label,
  ComponentLevelNotification,
} from '@americanexpress/dls-react';
import { validateDlsInput } from '../../Validations/Validations';
import { parseHtml, replaceDoubleWithSingleQuotes } from '../../utils/util';
import { getRequiredData } from '../../constants';

const getVal = require('lodash/get');

/**
 * returns DLS TextArea component
 * @param {*} props
 */
/* eslint complexity: ["error", 40] -- need to increase complexity */
const TextArea = (props) => {
  const {
    cssProperties,
    fieldType,
    fieldLabel,
    fieldPlaceholder,
    fieldId,
    fieldValidation,
    filledFormData,
    memberInfo,
    selectKey,
    preFilledWith,
    isPreFilled,
    fetchUrl,
    fieldRank,
    validationErrors,
    setValidationErrors,
    setFocus,
    fieldValue,
    validations = [],
    curPageValues,
    setCurPageValues,
    comments,
    setComments,
    entityToEdit,
    entityComments = [],
    entityCompositeKey,
    showId,
    idsToClear,
    isScrollTop = true,
    getReviewDisabled,
    formSubmissionPayload,
    objectOfFunctions,
    prefillStyleConfig,
  } = props;
  const [data, setData] = useState('');
  const [isValid, setIsValid] = useState(true);
  const [isUserEdited, setIsUserEdited] = useState(false);
  const payloadFieldValue = filledFormData ? filledFormData[fieldId] : undefined;
  let dlsClass = 'form-control';
  const [validationErrState, dispatchErrState] = useReducer(
    (prevState, payload) => ({ ...prevState, ...payload }),
    { validationErr: '', isValid: true }
  );
  const optionalLabel = fieldLabel
    && Object.prototype.hasOwnProperty.call(fieldLabel, 'optionalLabel')
    ? fieldLabel.optionalLabel
    : ' (Optional)';

  if (fieldValidation.bgColor) {
    dlsClass = `${dlsClass} ${fieldValidation.bgColor}`;
  }

  // Back button support
  useEffect(() => {
    if (typeof payloadFieldValue !== 'undefined') {
      setData(payloadFieldValue);
      return;
    }
    setData('');
  }, [fieldId, payloadFieldValue]);

  useEffect(() => {
    let preFilledVal = '';
    if (isPreFilled && preFilledWith === 'filledFormData' && !fetchUrl) {
      if (filledFormData) {
        preFilledVal = filledFormData[selectKey];
        setData(preFilledVal);
      }
    } else if (isPreFilled && preFilledWith === 'memberInfo' && !fetchUrl) {
      if (memberInfo) {
        preFilledVal = getRequiredData({
          isPreFilled,
          preFilledWith,
          fieldValue,
          selectKey,
          fetchUrl,
          filledFormData,
          memberInfo,
        });
        setData(preFilledVal);
      }
    } else if (fieldValue !== 'default' && fieldValue !== '') {
      preFilledVal = fieldValue;
      setData(preFilledVal);
    }
  }, [memberInfo, filledFormData]);

  /**
   * handle input value
   * @param {*} event
   */
  const handleInput = (event, val = null) => {
    if (comments && comments[fieldId]) {
      setIsValid(true);
      delete comments[fieldId];
      setComments({ ...comments });
    }

    if (event) {
      setIsValid(true);
    } else if (entityComments.length > 0 && entityCompositeKey) {
      const matchedEntity = entityComments.find(
        (obj) => obj.compositeKey === entityCompositeKey
          && (!showId || !obj.showId || obj.showId === showId)
      );
      if (matchedEntity && fieldId in matchedEntity) {
        // This isValid is for showing red borders for the comments from the review tool.
        setIsValid(false);
      }
    }

    const inputVal = event ? event.target.value : val;
    if (event) setIsUserEdited(true);
    if (validations.length > 0) {
      const invalidMsg = validateDlsInput(inputVal, validations);
      if (invalidMsg) {
        dispatchErrState({ validationErr: `${invalidMsg}`, isValid: false });
      } else {
        dispatchErrState({ validationErr: '', isValid: true });
      }
    }
    setData(inputVal);
    setCurPageValues({ ...curPageValues, [fieldId]: replaceDoubleWithSingleQuotes(inputVal) });

    if (comments && comments[fieldId]) {
      delete comments[fieldId];
      setComments({ ...comments });
    }

    if (event) {
      setIsValid(true);
    }
  };

  const handleOnBlur = (e) => {
    if (e?.target?.value) {
      setData(() => replaceDoubleWithSingleQuotes(e.target.value.trim()));
    }
  };

  useEffect(() => {
    setIsValid(true);
    if (entityToEdit && typeof entityToEdit[fieldId] !== 'undefined') {
      handleInput(null, entityToEdit[fieldId]);
    }
  }, [entityToEdit]);

  useEffect(() => {
    if (comments && comments[fieldId]) {
      setIsValid(false);
    }
  }, [comments]);

  const inputFocus = useCallback((inputElement) => {
    if (inputElement) {
      if (isScrollTop) {
        window.scrollTo(0, 0);
      }
    }
  }, []);

  const invalidHandler = (e) => {
    if (validations.length > 0) {
      e.preventDefault();
      handleInput(e);
      setFocus({ ...validationErrors, ...{ [fieldRank]: `${fieldId}` } });
    }
  };

  useEffect(() => {
    if (validationErrState.isValid && validationErrors[fieldRank]) {
      delete validationErrors[fieldRank];
      setValidationErrors({ ...validationErrors });
    } else if (!validationErrState.isValid) {
      setValidationErrors({
        ...validationErrors,
        ...{ [fieldRank]: `${fieldId}` },
      });
    }
  }, [validationErrState.isValid]);

  useEffect(() => {
    if (getVal(idsToClear.ids, fieldId)) setData('');
  }, [idsToClear.toggleState]);

  // Prefill style: resolved from formSubmissionPayload via matchAPIKey (full dotted path).
  // Row-level fields use {index} placeholder in matchAPIKey, replaced by __rawApiIndex.
  let showPrefillStyle = false;
  if (prefillStyleConfig) {
    /* eslint-disable-next-line no-underscore-dangle -- internal marker set during API prefill */
    const rawApiIndex = entityToEdit?.__rawApiIndex ?? 0;
    const resolvedKey = prefillStyleConfig.matchAPIKey?.replace('{index}', rawApiIndex);
    const apiValue = getVal(formSubmissionPayload, resolvedKey);
    const currentValueSource = getVal(objectOfFunctions, prefillStyleConfig.sourcePath);
    const currentValue = getVal(currentValueSource, fieldId);
    const hasPrefillSource = apiValue !== undefined && apiValue !== null && apiValue !== ''
      && apiValue === currentValue;
    showPrefillStyle = hasPrefillSource && !isUserEdited;
  }
  const showErrorMessage = (errorMessage) => (
    <ComponentLevelNotification
      id={`${fieldId}-errorMessage`}
      className="pad-1-t"
    >
      {parseHtml(errorMessage)}
    </ComponentLevelNotification>
  );

  const textAreaLabel = fieldLabel && fieldLabel.label && (
    <Label className={fieldLabel.cssProperties.style} htmlFor={fieldId}>
      {parseHtml(fieldLabel.label)}
      {!fieldValidation.required && optionalLabel && (
        <span className={fieldLabel.cssProperties.style}>
          {optionalLabel}
        </span>
      )}
      {fieldValidation && fieldValidation.isToolTip && (
        <>
          <span> </span>
          <Tooltip id={`${fieldId}-tooltip`}>
            <TooltipTrigger>
              <InfoTooltipButton screenReaderLabel="info" />
            </TooltipTrigger>
            <TooltipContent>
              {parseHtml(fieldValidation.toolTipInfo)}
            </TooltipContent>
          </Tooltip>
        </>
      )}
    </Label>
  );

  const prefillInlineStyle = showPrefillStyle ? prefillStyleConfig.style || {} : {};

  const renderElement = (
    <Textarea
      rows={cssProperties.rows}
      label={textAreaLabel}
      cols={cssProperties.cols || 100}
      className={`${dlsClass}${showPrefillStyle ? ` ${prefillStyleConfig.className || ''}` : ''}`}
      type={fieldType}
      id={fieldId}
      name={fieldId}
      value={data}
      placeholder={fieldPlaceholder}
      onChange={handleInput}
      onBlur={handleOnBlur}
      aria-disabled={fieldValidation.disabled || getReviewDisabled(fieldId)}
      required={fieldValidation.required}
      ref={inputFocus}
      maxLength={fieldValidation.maxSize}
      onInvalid={invalidHandler}
      style={{
        border: !isValid && data ? '.0625rem solid red' : 'none',
        ...prefillInlineStyle,
      }}
    />
  );

  return (
    <>
      {renderElement}
      {(fieldValidation.disabled || getReviewDisabled(fieldId)) && (
        <input type="hidden" id={`${fieldId}`} name={fieldId} value={data} />
      )}
      {validationErrState.validationErr
        && showErrorMessage(validationErrState.validationErr)}
      {comments && comments[fieldId]
        && showErrorMessage(comments[fieldId].comment)}
      {entityComments.length > 0
        && entityCompositeKey
        && (() => {
          const matchedEntity = entityComments.find(
            (obj) => obj.compositeKey === entityCompositeKey
              && (!showId || !obj.showId || obj.showId === showId) && !isValid
          );
          return matchedEntity && matchedEntity[fieldId]
            ? showErrorMessage(
              fieldId === 'compositeKey'
                ? matchedEntity[fieldId]
                : matchedEntity[fieldId].defaultValue
            )
            : null;
        })()}
    </>
  );
};

TextArea.propTypes = {
  fieldType: PropTypes.string,
  cssProperties: PropTypes.objectOf(PropTypes.shape),
  fieldLabel: PropTypes.objectOf(PropTypes.shape),
  fieldPlaceholder: PropTypes.string,
  fieldId: PropTypes.string,
  fieldValidation: PropTypes.objectOf(PropTypes.shape),
  preFilledWith: PropTypes.string,
  fetchUrl: PropTypes.string,
  filledFormData: PropTypes.objectOf(PropTypes.shape),
  memberInfo: PropTypes.objectOf(PropTypes.shape),
  selectKey: PropTypes.string,
  isPreFilled: PropTypes.string,
  setValidationErrors: PropTypes.func,
  validationErrors: PropTypes.objectOf(PropTypes.shape),
  validations: PropTypes.arrayOf(PropTypes.shape),
  setFocus: PropTypes.func,
  fieldRank: PropTypes.number,
  curPageValues: PropTypes.objectOf(PropTypes.shape),
  setCurPageValues: PropTypes.func,
  fieldValue: PropTypes.string,
  comments: PropTypes.objectOf(PropTypes.shape),
  setComments: PropTypes.func,
  entityToEdit: PropTypes.objectOf(PropTypes.shape),
  entityComments: PropTypes.arrayOf(PropTypes.shape),
  entityCompositeKey: PropTypes.string,
  showId: PropTypes.string,
  idsToClear: PropTypes.objectOf(PropTypes.shape),
  isScrollTop: PropTypes.bool,
  getReviewDisabled: PropTypes.func,
  formSubmissionPayload: PropTypes.objectOf(PropTypes.shape),
  objectOfFunctions: PropTypes.objectOf(PropTypes.shape),
  prefillStyleConfig: PropTypes.shape({
    sourcePath: PropTypes.string,
    matchAPIKey: PropTypes.string,
    style: PropTypes.objectOf(PropTypes.string),
    className: PropTypes.string,
  }),
};

export default TextArea;
